/**
 * Authentication Logging Utility
 *
 * Provides environment-aware logging for authentication operations.
 * - Development: Detailed logs with timestamps
 * - Production: Errors only
 * - SECURITY: Never logs token values
 */

const isDev = import.meta.env.MODE === 'development'

/**
 * Log authentication events (development only)
 * @param message - Log message
 * @param data - Optional data to log (never pass tokens!)
 */
export const authLog = (message: string, data?: any): void => {
  if (isDev) {
    const timestamp = new Date().toISOString()
    console.log(`[Auth] ${timestamp} ${message}`, data || '')
  }
}

/**
 * Log authentication errors (all environments)
 * @param message - Error message
 * @param error - Optional error object (never pass tokens!)
 */
export const authError = (message: string, error?: any): void => {
  const timestamp = new Date().toISOString()
  console.error(`[Auth Error] ${timestamp} ${message}`, error || '')
}

/**
 * Log token information without exposing the actual token value
 * @param tokenType - Type of token (access, refresh, etc.)
 * @param token - Token string (will only log length, not value)
 */
export const authLogToken = (tokenType: string, token: string | null): void => {
  if (isDev) {
    const timestamp = new Date().toISOString()
    if (token) {
      console.log(`[Auth] ${timestamp} ${tokenType} token present (length: ${token.length})`)
    } else {
      console.log(`[Auth] ${timestamp} ${tokenType} token missing`)
    }
  }
}

/**
 * Log preferences events (development only)
 * @param message - Log message
 * @param data - Optional data to log (NEVER pass passwords!)
 */
export const preferencesLog = (message: string, data?: any): void => {
  if (isDev) {
    const timestamp = new Date().toISOString()
    console.log(`[Preferences] ${timestamp} ${message}`, data || '')
  }
}

/**
 * Log preferences errors (all environments)
 * @param message - Error message
 * @param error - Optional error object
 */
export const preferencesError = (message: string, error?: any): void => {
  const timestamp = new Date().toISOString()
  console.error(`[Preferences Error] ${timestamp} ${message}`, error || '')
}

/**
 * Log preferences action with feature availability check
 * @param action - Action name (changePassword, addAlias, etc.)
 * @param featureAvailable - Whether the backend feature is available
 */
export const preferencesAction = (action: string, featureAvailable: boolean): void => {
  if (isDev) {
    const timestamp = new Date().toISOString()
    const status = featureAvailable ? '✓ Available' : '⚠️ Not Implemented'
    console.log(`[Preferences] ${timestamp} ${action}: ${status}`)
  }
}
